import { useState } from "react";
import { useCreatePolygon, useDeletePolygon } from "@/polygon/api/queries/polygon.queries.ts";
import { ICreatePolygonInput } from "@/polygon/api/requests/polygon.requests.ts";
import { calculateChecksum, extractEpsgFromDxf, validateDxfFile } from "@/polygon/utils/file-utils.ts";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface UploadForm {
  name: string;
  checksum: string;
  epsg_code: string;
  file: File | null;
}

export const usePolygonOperations = () => {
  const { t } = useTranslation("polygon");
  const createMutation = useCreatePolygon();
  const deleteMutation = useDeletePolygon();
  
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadForm, setUploadForm] = useState<UploadForm>({
    name: "",
    checksum: "",
    epsg_code: "",
    file: null,
  });

  const handleDelete = (id: number, name: string) => {
    if (window.confirm(t("operations.deleteConfirm", { name }))) {
      deleteMutation.mutate(id, {
        onSuccess: () => {
          toast.success(t("operations.deleteSuccess"));
        },
        onError: (error) => {
          console.error("Delete error:", error);
          toast.error(t("operations.deleteError"));
        },
      });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!validateDxfFile(file)) {
      toast.error(t("operations.invalidFile"));
      return;
    }

    setIsAnalyzing(true);
    setUploadForm(prev => ({ ...prev, file }));

    try {
      // Calculate checksum
      const checksum = await calculateChecksum(file);
      
      // Extract EPSG code
      const epsgCode = await extractEpsgFromDxf(file);
      
      // Auto-populate form fields
      setUploadForm(prev => ({
        ...prev,
        name: prev.name || file.name.replace('.dxf', ''),
        checksum,
        epsg_code: epsgCode?.toString() || "4326",
      }));

      toast.success(t("operations.analyzeSuccess"));
    } catch (error) {
      console.error("Error analyzing file:", error);
      toast.error(t("operations.analyzeError"));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.name || !uploadForm.checksum || !uploadForm.epsg_code) {
      toast.error(t("operations.missingFields"));
      return;
    }

    const data: ICreatePolygonInput = {
      name: uploadForm.name,
      checksum: uploadForm.checksum,
      epsg_code: parseInt(uploadForm.epsg_code, 10),
      file: uploadForm.file,
    };

    createMutation.mutate(data, {
      onSuccess: () => {
        toast.success(t("operations.uploadSuccess"));
        resetUploadForm();
      },
      onError: (error) => {
        console.error("Upload error:", error);
        toast.error(t("operations.uploadError"));
      },
    });
  };

  const resetUploadForm = () => {
    setUploadForm({
      name: "",
      checksum: "",
      epsg_code: "",
      file: null,
    });
  };

  const updateUploadForm = (updates: Partial<UploadForm>) => {
    setUploadForm(prev => ({ ...prev, ...updates }));
  };

  return {
    uploadForm,
    isAnalyzing,
    createMutation,
    deleteMutation,
    handleDelete,
    handleFileChange,
    handleUpload,
    resetUploadForm,
    updateUploadForm,
  };
};
